import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import {
  NzUploadChangeParam,
  NzUploadFile,
  NzUploadModule,
} from 'ng-zorro-antd/upload';
import { BehaviorSubject, Observable, Subject, take, takeUntil } from 'rxjs';
import { DRAWER_IDS } from '../../../core/constants/drawer-ids.constant';
import { ErrorResponse } from '../../../core/models/error.models';
import { DrawerService } from '../../../core/services/drawer.service';
import { CommonService } from '../../../shared/services/common.service';
import { InsightMode } from '../insights.enums';
import { CaseDetailModel, EditCaseDrawerParams } from '../insights.interface';
import { InsightsService } from '../insights.service';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { AuditContextService } from '../../audit-logs/audit-context.service';

@Component({
  selector: 'app-student-image',
  standalone: true,
  imports: [
    NzUploadModule,
    NzButtonModule,
    NzIconModule,
    SvgIconComponent,
    CommonModule,
    NzSpinModule,
  ],
  templateUrl: './student-image.component.html',
  styleUrl: './student-image.component.scss',
})
export class StudentImageComponent implements OnDestroy {
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  loading$: Observable<boolean> = this.loadingSubject.asObservable();
  private readonly destroy$ = new Subject<void>();
  readonly MAX_FILE_SIZE = 5 * 1024 * 1024;
  fileList: NzUploadFile[] = [];
  previewImage: string | null = null;
  isUploadingImage = false;

  get loading(): boolean {
    return this.isUploadingImage || this.loadingSubject.value;
  }

  getBase64 = (file: File): Promise<string | ArrayBuffer | null> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error); //NOSONAR
    });
  caseParams: EditCaseDrawerParams = {
    case: {} as CaseDetailModel,
    mode: InsightMode.CREATE,
  };

  constructor(
    private readonly drawerService: DrawerService,
    private readonly insightsService: InsightsService,
    private readonly cd: ChangeDetectorRef,
    private readonly commonService: CommonService,
    private readonly auditContextService: AuditContextService
  ) {
    this.setupDrawerSubscription();
  }

  private setupDrawerSubscription(): void {
    this.loadingSubject.next(true);
    this.drawerService
      .getParams<EditCaseDrawerParams>(DRAWER_IDS.STUDENT_PROFILE_IMAGE)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (params: EditCaseDrawerParams | null) => {
          if (!params) return;
          this.caseParams = params;
          this.previewImage = this.caseParams.case?.student?.imageUrl || null;
          this.loadingSubject.next(false);
          this.cd.detectChanges();
        },
        error: () => {
          this.loadingSubject.next(false);
          this.cd.detectChanges();
        },
      });
  }

  beforeUpload = (file: NzUploadFile): boolean /* NOSONAR */ => {
    if (!file) {
      this.commonService.notify('error', 'No file selected');
      return false;
    }

    if (file.size! > this.MAX_FILE_SIZE) {
      this.commonService.notify('error', 'File size cannot exceed 5MB');
      this.fileList = [];
      this.previewImage = this.caseParams.case?.student?.imageUrl || null;
      return false;
    }

    if (
      !file.type ||
      !['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)
    ) {
      this.commonService.notify(
        'error',
        'Image file must be a valid image file (e.g., .jpg, .png, .jpeg).'
      );
      this.fileList = [];
      this.previewImage = this.caseParams.case?.student?.imageUrl || null;
      return false;
    }

    this.fileList = [file];
    this.createImagePreview(file);
    return false;
  };

  handleChange = async (event: NzUploadChangeParam) => {
    if (event.file.status === 'done' || event.file.status === 'uploading') {
      this.createImagePreview(event.file);
    }
  };

  createImagePreview(file: NzUploadFile) {
    this.loadingSubject.next(true);
    const reader = new FileReader();
    reader.readAsDataURL(file as unknown as Blob);
    reader.onload = () => {
      this.previewImage = reader.result as string;
      this.loadingSubject.next(false);
      this.cd.detectChanges();
    };
    reader.onerror = () => {
      this.loadingSubject.next(false);
      this.cd.detectChanges();
    };
  }

  handleRemove = (): boolean => {
    this.fileList = [];
    return true;
  };

  onEdit() {
    const file = this.fileList[0] as unknown as File;
    const studentId = this.caseParams.case?.studentId;
    if (!studentId) {
      return;
    }
    if (!file) {
      this.commonService.notify('error', 'Please select a file');
      return;
    }

    this.isUploadingImage = true;
    this.loadingSubject.next(true);

    // Build modified fields for audit logging
    const oldImageUrl = this.caseParams.case?.student?.imageUrl || null;
    const newFileName = file.name || 'uploaded-image';
    const _modifiedFields = this.auditContextService.buildFieldChanges(
      { image: oldImageUrl },
      { image: newFileName },
      {
        sensitiveFields: ['image'],
        labelMap: { image: 'Student Image' },
        excludeFields: [],
      }
    );

    const action$ = this.insightsService.updateStudentImage(
      studentId as number,
      this.fileList[0] as unknown as File,
      _modifiedFields
    );

    action$.pipe(take(1), takeUntil(this.destroy$)).subscribe({
      next: res => {
        this.commonService.notify(
          'success',
          `Student Image Uploaded Successfully`,
          '',
          '450px'
        );
        const drawer = this.drawerService.getDrawerRef(
          DRAWER_IDS.STUDENT_PROFILE_IMAGE
        );
        drawer?.close({ url: res });
        this.isUploadingImage = false;
        this.loadingSubject.next(false);
      },
      error: ({ errors, message }: ErrorResponse) => {
        const errorMessages = errors.map(
          err => err.description || err.detailedMessage
        );
        this.commonService.notify('error', errorMessages, message);
        this.isUploadingImage = false;
        this.loadingSubject.next(false);
      },
    });
  }

  closeDrawer(): void {
    this.drawerService.closeDrawer(DRAWER_IDS.STUDENT_PROFILE_IMAGE);
  }

  ngOnDestroy(): void {
    this.loadingSubject.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
